/**
 * Edit User Dialog
 *
 * Reason: Allows admins to edit existing user's role and status
 */

import { FC, useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { useQueryClient } from 'react-query';
import { useUpdateOrgUser, OrgUser, LIST_ORG_USERS_CACHE_KEY } from '@/adapters/users-v2';

interface EditUserDialogProps {
  open: boolean;
  user: OrgUser | null;
  onClose(): void;
}

/**
 * Dialog for editing existing user details
 *
 * @reason Provides form UI for admins to update user role and name
 * Note: Status is managed by backend only, not editable from frontend
 */
const EditUserDialog: FC<EditUserDialogProps> = ({ open, user, onClose }) => {
  const [name, setName] = useState('');
  const [role, setRole] = useState<'admin' | 'manager' | 'viewer'>('manager');
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const updateUserMutation = useUpdateOrgUser(user?.id || '');

  /**
   * Syncs form state with user prop
   *
   * @reason Updates form when user changes or dialog opens
   */
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setRole(user.role);
      // Note: Status is managed by backend only
    }
  }, [user]);

  /**
   * Handles form submission to update user
   *
   * @reason Updates user details and refreshes the user list
   */
  const handleUpdate = async (): Promise<void> => {
    if (!user) return;

    setError(null);

    try {
      await updateUserMutation.mutateAsync({
        name: name.trim() || undefined,
        role,
        // Note: isActive/status is managed by backend only
      });

      // Invalidate user list to refresh
      await queryClient.invalidateQueries(LIST_ORG_USERS_CACHE_KEY);

      // Close dialog and reset form
      handleClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user';
      setError(errorMessage);
    }
  };

  /**
   * Handles dialog close and resets form
   *
   * @reason Cleans up form state when dialog is closed
   */
  const handleClose = (): void => {
    setError(null);
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          margin="dense"
          label="Email"
          type="email"
          fullWidth
          value={user.email}
          disabled
          sx={{ mb: 2 }}
          helperText="Email cannot be changed"
        />

        <TextField
          margin="dense"
          label="Name"
          type="text"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={updateUserMutation.isLoading}
          sx={{ mb: 2 }}
        />

        <TextField
          margin="dense"
          label="Role"
          select
          fullWidth
          required
          value={role}
          onChange={(e) => setRole(e.target.value as 'admin' | 'manager' | 'viewer')}
          disabled={updateUserMutation.isLoading}
          sx={{ mb: 2 }}
        >
          <MenuItem value="admin">Admin - Full access to organization</MenuItem>
          <MenuItem value="manager">Manager - Can edit events and projects</MenuItem>
          <MenuItem value="viewer">Viewer - Read-only access</MenuItem>
        </TextField>

        {/* Note: Status is managed by backend only, not editable from frontend */}

        {updateUserMutation.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={updateUserMutation.isLoading}>
          Cancel
        </Button>
        <Button onClick={handleUpdate} variant="contained" disabled={updateUserMutation.isLoading}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
